import FilterListIcon from '@mui/icons-material/FilterList';
import type { Theme } from '@mui/material';
import { Box, Button, Collapse } from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FilterKey } from '@/pages/ValuationsV2/components/Filters/filterConfig';
import { VALUATIONS_CONTEXT } from '../../constants';
import {
  useValuationsStore,
  type ValuationsFilters as ValuationsFiltersType,
} from '../../store/valuationsStore';
import { AssetsFilter } from './AssetsFilter';
import { BasinsFilter } from './BasinsFilter';
import { ChipsContainer } from './ChipsContainer';
import { CompaniesFilter } from './CompaniesFilter';
import { CountriesFilter } from './CountriesFilter';
import { FilterBarComposable } from './FilterBarWrapper';
import { FilterSwitches } from './FilterSwitches';
import { HydrocarbonsFilter } from './HydrocarbonsFilter';
import { MoreFilters } from './MoreFilters';
import { ShoreStatusesFilter } from './ShoreStatusesFilter';

const buttonSectionStyles = (theme: Theme) => ({
  display: 'flex',
  gap: 'inherit',
  flexWrap: 'wrap',
  alignItems: 'center',
  flexShrink: '0',
  [theme.breakpoints.down('lg')]: {
    justifyContent: 'flex-start',
    flexDirection: 'row-reverse',
  },
});

type FiltersProps = {
  filters: ValuationsFiltersType;
  setFilters: React.Dispatch<React.SetStateAction<ValuationsFiltersType>>;
  children: ReactNode;
};

const FiltersRoot = ({ filters, setFilters, children }: FiltersProps) => {
  const handleControlChange = useCallback(
    (value: string | boolean, key: string) => {
      setFilters((currentFilters: ValuationsFiltersType) => ({
        ...currentFilters,
        [key]: value,
      }));
    },
    [setFilters],
  );

  const onFilterChange = useCallback(
    <K extends keyof ValuationsFiltersType>(key: K) =>
      (value: ValuationsFiltersType[K]) => {
        setFilters((currentFilters: ValuationsFiltersType) => ({
          ...currentFilters,
          [key]: value,
        }));
      },
    [setFilters],
  );

  return (
    <FilterBarComposable.Root data-id='valuations-filters-root'>
      <FilterBarComposable.Filters data-id='valuations-filters-grid'>
        <CountriesFilter
          filters={filters}
          onChange={onFilterChange(FilterKey.Countries)}
          context={VALUATIONS_CONTEXT}
        />
        <AssetsFilter
          filters={filters}
          onChange={onFilterChange(FilterKey.Assets)}
          context={VALUATIONS_CONTEXT}
        />
        <CompaniesFilter
          filters={filters}
          onChange={onFilterChange(FilterKey.Companies)}
          onControlChange={handleControlChange}
          context={VALUATIONS_CONTEXT}
        />
        <BasinsFilter
          filters={filters}
          onChange={onFilterChange(FilterKey.Basins)}
          context={VALUATIONS_CONTEXT}
        />
        <ShoreStatusesFilter
          filters={filters}
          onChange={onFilterChange(FilterKey.ShoreStatuses)}
          context={VALUATIONS_CONTEXT}
        />
        <HydrocarbonsFilter filters={filters} onChange={onFilterChange(FilterKey.Hydrocarbons)} />
      </FilterBarComposable.Filters>
      <FilterBarComposable.Actions data-id='valuations-filters-actions'>
        <Box sx={buttonSectionStyles} data-id='valuations-filters-button-section'>
          {children}
        </Box>
      </FilterBarComposable.Actions>
    </FilterBarComposable.Root>
  );
};

const MoreFiltersContainer = () => {
  const filters = useValuationsStore.use.filters();
  const setFilters = useValuationsStore.use.setFilters();

  const onFilterChange = useCallback(
    <K extends keyof ValuationsFiltersType>(key: K) =>
      (value: ValuationsFiltersType[K]): void => {
        setFilters((currentFilters: ValuationsFiltersType) => ({
          ...currentFilters,
          [key]: value,
        }));
      },
    [setFilters],
  );

  const selectedCountries = useMemo(() => filters[FilterKey.Countries] || [], [filters]);

  return (
    <MoreFilters
      filters={filters}
      onChange={onFilterChange}
      selectedCountries={selectedCountries}
    />
  );
};

const Filters = () => {
  const filters = useValuationsStore.use.filters();
  const setFilters = useValuationsStore.use.setFilters();
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  const toggleMoreFilters = useCallback(() => {
    setMoreFiltersOpen(prev => !prev);
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 20,
        background: (theme: Theme) => theme.palette.baseColors.white.c100,
        borderBottom: (theme: Theme) => `solid ${theme.palette.baseColors.iceBlue.c100} 1px`,
      }}
    >
      <FiltersRoot filters={filters} setFilters={setFilters}>
        <FilterSwitches filters={filters} setFilters={setFilters} />
        <Button
          color='secondary'
          startIcon={<FilterListIcon />}
          onClick={toggleMoreFilters}
          data-id='more-filters-toggle-button'
          sx={{
            minWidth: '9rem',
            backgroundColor: (theme: Theme) =>
              moreFiltersOpen ? theme.palette.baseColors.iceBlue.c100 : 'inherit',
          }}
        >
          {moreFiltersOpen ? 'Less Filters' : 'More Filters'}
        </Button>
      </FiltersRoot>

      <Collapse in={moreFiltersOpen} timeout='auto' unmountOnExit>
        <MoreFiltersContainer />
      </Collapse>
      <ChipsContainer filters={filters} setFilters={setFilters} />
    </Box>
  );
};

export { Filters };
export type { FiltersProps };
